import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import { Menu, X } from "lucide-react";
import { API_URL, Button, BarreNavigation } from "./Dashboard";

const Kahoot = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [joueurs, setJoueurs] = useState([]);
  const [montrerAjouterPoints, setMontrerAjouterPoints] = useState(false);
  const [montrerSupprimerScores, setMontrerSupprimerScores] = useState(false);
  const [membres, setMembres] = useState([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (!storedUser) navigate("/");
    else setUser(storedUser);

    fetch(`${API_URL}/obtenirScoresKahoot.php`)
      .then((res) => res.json())
      .then((data) => setJoueurs(data))
      .catch(console.error);

    fetch(`${API_URL}/obtenirMembresKahoot.php`)
      .then((res) => res.json())
      .then((data) => setMembres(data))
      .catch(console.error);
  }, [navigate]);

  if (!user) return null;

  const estSecretaire =
    ["Secrétaire général", "Secrétaire générale"].includes(user.poste);

  const handleLogout = () => {
    localStorage.removeItem("user");
    navigate("/");
  };

  return (
    <div className="flex min-h-screen bg-gray-50 relative overflow-x-hidden">
        
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <div className={`
        fixed inset-y-0 left-0 z-30 w-64 bg-white shadow-md transform transition-transform duration-300 ease-in-out
        lg:relative lg:translate-x-0
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
         <BarreNavigation 
            user={user} 
            deconnexion={handleLogout}
            onNavigate={() => setIsMobileMenuOpen(false)} 
         />
         <button 
            onClick={() => setIsMobileMenuOpen(false)}
            className="absolute top-4 right-4 lg:hidden text-gray-500"
        >
            <X size={24} />
        </button>
      </div>

      <main className="flex-1 p-4 lg:p-8 w-full">
        <header className="flex items-center justify-between mb-6 gap-4">
          <div className="flex items-center gap-3">
             <button 
                className="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                onClick={() => setIsMobileMenuOpen(true)}
            >
                <Menu size={24} />
            </button>
            <h1 className="text-2xl font-bold text-gray-800">Classement Kahoot</h1>
          </div>

          {estSecretaire ? (
            <div className="flex gap-2">
              <Button onClick={() => setMontrerAjouterPoints(true)} title="Ajouter des points" className="flex items-center justify-center">
                <LucideIcons.Plus size={16} className="sm:mr-2"/>
                <span className="hidden sm:inline">Ajouter des points</span>
              </Button>

              <Button variant="outline" onClick={() => setMontrerSupprimerScores(true)} title="J'ai fait une erreur" className="text-red-600 border-red-400 hover:bg-red-100 flex items-center justify-center">
                <LucideIcons.XCircle size={16} className="sm:mr-2"/>
                <span className="hidden sm:inline">J'ai fait une erreur</span>
              </Button>
            </div>
          ) : (
            <div className="flex items-center space-x-4">
              <Button variant="outline" onClick={handleLogout} title="Se déconnecter">
                <span className="hidden sm:inline">Se déconnecter</span>
                <LucideIcons.LogOut size={16} className="sm:hidden" />
              </Button>
            </div>
          )}
        </header>

        {montrerSupprimerScores && (
            <SupprimerScores onClose={() => setMontrerSupprimerScores(false)}/>
        )}

        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {joueurs.length === 0 ? (
            <p className="text-gray-500 col-span-full text-center">
              Aucun score enregistré pour ce mois.
            </p>
          ) : (
            joueurs.map((membre, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow p-4 flex flex-col items-center hover:bg-gray-50 transition-colors"
              >
                <img
                  src={`/images/PhotosMandat/${membre.prenom} ${membre.nom}.avif`}
                  alt={`${membre.prenom} ${membre.nom}`}
                  className="w-16 h-16 rounded-full object-cover border mb-2"
                />
                <p className="font-semibold text-gray-800 text-center truncate w-full">
                  {membre.prenom} {membre.nom}
                </p>
                <p className="text-sm text-gray-500">{membre.score_total} pts</p>
                <span className={`font-semibold text-sm mt-1 px-2 py-0.5 rounded-full
                    ${index === 0 ? 'bg-yellow-100 text-yellow-700' : 
                      index === 1 ? 'bg-gray-100 text-gray-600' :
                      index === 2 ? 'bg-orange-100 text-orange-700' : 'text-gray-400'
                    }
                `}>
                  #{index + 1}
                </span>
              </div>
            ))
          )}
        </section>
      </main>

      {montrerAjouterPoints && (
        <AjouterPoints onClose={() => setMontrerAjouterPoints(false)} membres={membres} />
      )}
    </div>
  );
};

const AjouterPoints = ({ onClose, membres }) => {
  const today = new Date();
  const [jour, setJour] = useState(today.getDate());
  const [mois, setMois] = useState(today.getMonth() + 1);
  const [annee, setAnnee] = useState(today.getFullYear());
  const [points, setPoints] = useState(
    Object.fromEntries(membres.map((m) => [m.id, ""]))
  );

  const handleChange = (id, value) => {
    setPoints({ ...points, [id]: value });
  };

  const jours = Array.from({ length: 31 }, (_, i) => i + 1);
  const moisNoms = [
    "Janvier","Février","Mars","Avril","Mai","Juin",
    "Juillet","Août","Septembre","Octobre","Novembre","Décembre"
  ];

  const mettreAJourScores = (e) => {
    e.preventDefault();
    const date = `${annee}-${String(mois).padStart(2, "0")}-${String(jour).padStart(2, "0")}`;
    const scores = Object.entries(points)
      .filter(([_, val]) => val !== "")
      .map(([id, val]) => ({ id: parseInt(id), points: parseInt(val) }));

    if (scores.length === 0) return alert("Aucun score renseigné.");

    fetch(`${API_URL}/ajouterScoresKahoot.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ date, scores }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          alert("Scores mis à jour avec succès !");
          onClose();
          window.location.reload();
        } else {
          alert(data.error || "Erreur inconnue");
        }
      })
      .catch(console.error);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">Ajouter des points</h2>
        <form onSubmit={mettreAJourScores} className="space-y-4">
          {/* Sélection de la date */}
          <div className="flex flex-wrap gap-2">
            <select value={jour} onChange={(e) => setJour(e.target.value)} className="border rounded-lg px-2 py-1">
              {jours.map((j) => <option key={j} value={j}>{j}</option>)}
            </select>
            <select value={mois} onChange={(e) => setMois(e.target.value)} className="border rounded-lg px-2 py-1">
              {moisNoms.map((m, index) => <option key={index + 1} value={index + 1}>{m}</option>)}
            </select>
            <select value={annee} onChange={(e) => setAnnee(e.target.value)} className="border rounded-lg px-2 py-1">
              {[today.getFullYear()].map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>

          {/* Grille des membres pour saisir les points */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            {membres.map((m) => (
              <div key={m.id} className="bg-gray-50 rounded-xl p-3 flex flex-col items-center shadow">
                <img
                  src={`/images/PhotosMandat/${m.prenom} ${m.nom}.avif`}
                  alt={m.prenom}
                  className="w-12 h-12 md:w-16 md:h-16 rounded-full border object-cover mb-2"
                />
                <div className="text-center font-semibold text-sm">
                  <p className="truncate w-full">{m.prenom}</p>
                  <p className="truncate w-full">{m.nom}</p>
                </div>
                <input
                  type="number"
                  min="0"
                  className="w-full border rounded-lg px-2 py-1 text-center mt-2"
                  placeholder="Pts"
                  value={points[m.id]}
                  onChange={(e) => handleChange(m.id, e.target.value)}
                />
              </div>
            ))}
          </div>

          {/* Boutons */}
          <div className="flex justify-end space-x-3 pt-4 border-t mt-4">
            <Button variant="outline" type="button" onClick={onClose}>Annuler</Button>
            <Button type="submit">Enregistrer</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const SupprimerScores = ({ onClose }) => {
  const [dates, setDates] = useState([]);
  const [dateSelectionnee, setDateSelectionnee] = useState("");

  useEffect(() => {
    fetch(`${API_URL}/recupererDatesKahoot.php`)
      .then((res) => res.json())
      .then((data) => setDates(data))
      .catch(console.error);
  }, []);

  const supprimerScores = (e) => {
    e.preventDefault();
    if (!dateSelectionnee) return alert("Veuillez sélectionner une date.");

    if (!window.confirm(`Voulez-vous vraiment supprimer tous les scores du ${dateSelectionnee} ?`)) return;

    fetch(`${API_URL}/supprimerScoresKahoot.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ date: dateSelectionnee }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          alert("Scores supprimés avec succès !");
          onClose();
          window.location.reload();
        } else {
          alert(data.error || "Erreur inconnue");
        }
      })
      .catch(console.error);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-semibold mb-4">Supprimer des scores</h2>
        <form onSubmit={supprimerScores} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Sélectionnez la date</label>
            <select
              value={dateSelectionnee}
              onChange={(e) => setDateSelectionnee(e.target.value)}
              className="w-full border rounded-lg px-3 py-2"
              required
            >
              <option value="">-- Choisir une date --</option>
              {dates.map((d) => (
                <option key={d.date} value={d.date}>{d.date}</option>
              ))}
            </select>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="outline" type="button" onClick={onClose}>Annuler</Button>
            <Button type="submit" className="bg-red-600 hover:bg-red-700">Supprimer</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Kahoot;